// studioDrop — what dropping one card on another does (Studio v1, D2/D3/D6,
// B-05/B-06; pen "Studio – detail design" §8). Pure: the dragged card × the
// card under the pointer → one case.
//
//   dragged ↓ / target →   leaf         module          course
//   leaf                   new module   next part       next part (*)
//   module                 move         new course      next part
//   course                 move         move            move
//
//   - a leaf is a material, a source or a file (D3);
//   - new module / new course: a container of both, the target first, made
//     as the Container tool makes one; its card goes where the target was.
//     While the drop is pending its kind can be switched (module ↔ course);
//   - next part: the container's part N; (*) a loose leaf in a course is
//     allowed and marks the course not conforming (D6);
//   - a card already in that container: refused (the domain says why); not a
//     valid target, so no highlight;
//   - anything else (onto itself, nothing under the pointer, a combination no
//     preset has): a plain move.
//
// The drop maps to the same commands as the card menu (D14).

export type { DropCase };
export {
    applyDrop,
    classifyDrop,
    dropAt,
    dropTargetOf,
    isDropTarget,
    NO_DROP,
    switchKind,
};

import { exhaustSwitchCase } from "@bilda/utils";
import type { ContainerKind } from "../../app/containerFacts.js";
import type { ContainerCard, StudioCard } from "./studioCards.js";
import type { Placement, Point } from "./studioLayout.js";

type DropCase =
    | {
          readonly type: "new_container";
          readonly kind: ContainerKind;
          // Reading order: the target, then the dropped card.
          readonly parts: readonly [string, string];
          readonly target: string;
      }
    | {
          readonly type: "add_as_part";
          readonly container: string;
          readonly part: string;
          // The part's number once added (1-based).
          readonly number: number;
      }
    | {
          readonly type: "already_part";
          readonly container: string;
          readonly part: string;
      }
    | { readonly type: "move" };

const NO_DROP: DropCase = { type: "move" };

// What the dragged card is, for a drop (D3: materials, sources and files are
// leaves).
type Dragged = "leaf" | "module" | "other";

const draggedAs = (card: StudioCard): Dragged => {
    switch (card.type) {
        case "material":
        case "source":
        case "generic":
            return "leaf";
        case "container":
            return card.kind === "module" ? "module" : "other";
        default:
            return exhaustSwitchCase(card);
    }
};

const addTo = (target: ContainerCard, part: string): DropCase =>
    target.parts.some((p) => p.id === part)
        ? { type: "already_part", container: target.id, part }
        : {
              type: "add_as_part",
              container: target.id,
              part,
              number: target.parts.length + 1,
          };

const combine = (
    kind: ContainerKind,
    target: string,
    dragged: string,
): DropCase => ({
    type: "new_container",
    kind,
    parts: [target, dragged],
    target,
});

// A drop on a container: by the target's kind and what is dropped.
const onContainer = (
    dragged: Dragged,
    part: string,
    target: ContainerCard,
): DropCase => {
    switch (target.kind) {
        case "module":
            switch (dragged) {
                case "leaf":
                    return addTo(target, part);
                case "module":
                    return combine("course", target.id, part);
                case "other":
                    return NO_DROP;
                default:
                    return exhaustSwitchCase(dragged);
            }
        case "course":
            return dragged === "other" ? NO_DROP : addTo(target, part);
        default:
            return exhaustSwitchCase(target.kind);
    }
};

const classifyDrop = (dragged: StudioCard, target: StudioCard): DropCase => {
    if (dragged.id === target.id) return NO_DROP;
    const as = draggedAs(dragged);
    switch (target.type) {
        case "material":
        case "source":
        case "generic":
            return as === "leaf"
                ? combine("module", target.id, dragged.id)
                : NO_DROP;
        case "container":
            return onContainer(as, dragged.id, target);
        default:
            return exhaustSwitchCase(target);
    }
};

// The pending drop with the other kind of new container (module ↔ course);
// any other case is unchanged.
const switchKind = (drop: DropCase): DropCase =>
    drop.type === "new_container"
        ? { ...drop, kind: drop.kind === "module" ? "course" : "module" }
        : drop;

const contains = (p: Placement, at: Point): boolean =>
    at.x >= p.x &&
    at.x <= p.x + p.width &&
    at.y >= p.y &&
    at.y <= p.y + p.height;

// The drop at `at` (canvas units) for the card `draggedId`: the topmost other
// card under it (the last drawn), classified.
const dropAt = (
    cards: readonly StudioCard[],
    draggedId: string,
    at: Point,
    placements: ReadonlyMap<string, Placement>,
): DropCase => {
    const dragged = cards.find((c) => c.id === draggedId);
    const target = cards
        .filter((c) => {
            const p = placements.get(c.id);
            return c.id !== draggedId && p !== undefined && contains(p, at);
        })
        .at(-1);
    return dragged === undefined || target === undefined
        ? NO_DROP
        : classifyDrop(dragged, target);
};

// The canvas's intents a drop maps to.
type DropIntents = {
    // The dragged card to where it was dropped.
    readonly move: () => void;
    readonly putInNew: (
        kind: ContainerKind,
        ids: readonly string[],
        at: Point,
    ) => void;
    readonly addToContainer: (
        containerId: string,
        ids: readonly string[],
    ) => void;
};

const applyDrop = (
    drop: DropCase,
    placements: ReadonlyMap<string, Placement>,
    intents: DropIntents,
): void => {
    switch (drop.type) {
        case "new_container": {
            // The new container's card goes where the target was.
            const at = placements.get(drop.target);
            if (at === undefined) intents.move();
            else intents.putInNew(drop.kind, drop.parts, { x: at.x, y: at.y });
            return;
        }
        // An already-held part is sent too: the domain refuses it and says
        // why (the card was not highlighted); nothing moves.
        case "add_as_part":
        case "already_part":
            intents.addToContainer(drop.container, [drop.part]);
            return;
        case "move":
            intents.move();
            return;
        default:
            exhaustSwitchCase(drop);
    }
};

// The card highlighted as where the drop goes: only a valid target.
const dropTargetOf = (drop: DropCase): string | null => {
    switch (drop.type) {
        case "new_container":
            return drop.target;
        case "add_as_part":
            return drop.container;
        case "already_part":
        case "move":
            return null;
        default:
            return exhaustSwitchCase(drop);
    }
};

const isDropTarget = (drop: DropCase, id: string): boolean =>
    dropTargetOf(drop) === id;
